import type { InvoiceSummary, StripeCustomerInfos } from '@racemap/sdk/schema/billing';
import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { EventTypes } from '@racemap/sdk/schema/events';
import {
  type User,
  type UserOverview,
  UserOverviewSchema,
  UserSchema,
} from '@racemap/sdk/schema/user';
import { HTTPFetchError, RacemapAPIClient } from '@racemap/utilities/api-client';
import {
  MaxMobileWindowWidth,
  MaxNarrowWindowWidth,
  MaxTinyWindowWidth,
} from '@racemap/utilities/consts/common';
import { OneMinuteInMillis } from '@racemap/utilities/consts/time';
import { isEmptyString, shortIdBuilder } from '@racemap/utilities/functions/utils';
import type { RacemapEvent, Shadowtrack } from '@racemap/utilities/types/events';
import type { EventTrackObject } from '@racemap/utilities/types/geos';
import type { LatLng } from '@racemap/utilities/types/trackping';
import type { ObjectId, PathInto, TypeOfPath } from '@racemap/utilities/types/utils';
import type { Immutable } from 'immer';
import React, {
  type RefObject,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router';
import useSWR, { type SWRConfiguration } from 'swr';
import { useEventListener, useWindowSize } from 'usehooks-ts';
import { z } from 'zod';
import { makeGetter } from '../FormUtils';
import { type Platforms, getPlatformOfUserAgent } from '../userAgent';

export { useControlled } from './useControlled';
export { useImageUpload } from './useImageUpload';
export { useSimCardsPaginated } from './useSimCardsPaginated';
export { useSimCard } from './useSimCard';
export { useEventLoads } from './useEventLoads';
export { useRecentColors } from './useRecentColors';

const apiClient = RacemapAPIClient.fromWindowLocation();

export const useIsMobile = () => useIsWindowUpTo(MaxMobileWindowWidth);

export const useIsNarrowWindow = () => useIsWindowUpTo(MaxNarrowWindowWidth);

export const useIsTinyWindow = () => useIsWindowUpTo(MaxTinyWindowWidth);

const useIsWindowUpTo = (maxWidth: number) => {
  const { width: windowWith } = useWindowSize();

  // shortly after load windowWith is often 0
  return windowWith > 0 && windowWith <= maxWidth;
};

export function useURLSearchParams() {
  const { search } = useLocation();
  return new URLSearchParams(search);
}

export function useURLSearchParamUpdater<T extends Array<string> | string>() {
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  return (key: string, value: T | null | undefined) => {
    const params = new URLSearchParams(search);

    if (value == null || isEmptyString(value) || (Array.isArray(value) && value.length === 0)) {
      params.delete(key);
    } else if (Array.isArray(value)) {
      params.set(key, value.join(','));
    } else {
      params.set(key, value);
    }
    navigate({ pathname, search: params.toString() });
  };
}

export function useElementDimensions(myRef: React.RefObject<HTMLElement | null>) {
  const [dimensions, setDimensions] = useState({ width: 0, height: 0, visible: false });

  useEffect(() => {
    const getDimensions = () => ({
      width: myRef.current?.offsetWidth || 0,
      height: myRef.current?.offsetHeight || 0,
      visible: myRef && myRef.current?.offsetHeight !== 0 && myRef.current?.offsetWidth !== 0,
    });

    const handleResize = () => {
      setDimensions(getDimensions());
    };

    const resizeObserver = new ResizeObserver((_) => {
      handleResize();
    });

    if (myRef.current) {
      resizeObserver.observe(myRef.current);
      setDimensions(getDimensions());
    }

    return () => {
      if (myRef.current) {
        resizeObserver.unobserve(myRef.current);
      }
      resizeObserver.disconnect();
    };
  }, [myRef]);

  return dimensions;
}

export type FieldGenerator<
  OI extends { id: string | ObjectId },
  PI extends PathInto<OI> = PathInto<OI>,
> = <O extends object = OI, P extends PI = PI>(
  key: P,
) => {
  value: TypeOfPath<O, P>;
  onChange: (newValue: TypeOfPath<O, P>) => Promise<void>;
};

export type UpdaterFunction<T extends { id: string | ObjectId }> = (
  obj: T['id'],
  update: Array<{ key: PathInto<T>; newValue: any }>,
) => Promise<void>;

export function getFieldGenerator<T extends { id: string | ObjectId }, P extends PathInto<T>>(
  obj: T,
  updateFunction: UpdaterFunction<T>,
): FieldGenerator<T, P> {
  const makeChanger = (key: P) => (newValue: TypeOfPath<T, P>) =>
    updateFunction(obj.id, [{ key, newValue }]);
  const fieldGenerator = ((key: P) => ({
    value: makeGetter(obj)(key),
    onChange: makeChanger(key),
  })) as FieldGenerator<T, P>;

  return fieldGenerator as FieldGenerator<T, P>;
}

const UseUserResponseSchema = z.union([UserSchema, UserOverviewSchema]);
export function useUser({
  userId,
  options,
}: { userId?: string | ObjectId | null; options?: SWRConfiguration }): {
  user?: User | UserOverview | null;
  isLoading: boolean;
  error: Error;
} {
  const { data, isLoading, error } = useSWR(
    userId != null ? ['GET', 'USER', userId?.toString()] : null,
    async ([, , userId]) => apiClient.getUser(userId),
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
      ...options,
    },
  );
  const user = data != null ? UseUserResponseSchema.parse(data) : data;
  return { user, isLoading, error };
}

export function useUserInfo(
  userId: string | ObjectId | null | undefined,
  email?: string,
  options: SWRConfiguration = {},
): {
  user?: UserOverview | null;
  error?: Error;
  isLoading: boolean;
  isUnknownUser: boolean;
} {
  const res = useSWR(
    userId != null || email != null ? ['GET', 'USER_INFO', userId?.toString(), email] : null,
    async ([, , userId, email]) => {
      try {
        return UserOverviewSchema.parse(await apiClient.userLookup({ email, userId }));
      } catch (error) {
        if (error instanceof HTTPFetchError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
      keepPreviousData: true,
      ...options,
    },
  );

  return {
    user: res.data,
    error: res.error,
    isLoading: res.isLoading,
    isUnknownUser: res.data === null,
  };
}

export function useUserSearch(
  searchValue: string | null,
  limit = 5,
  options: SWRConfiguration = {},
): {
  users?: Array<UserOverview> | null;
  error?: Error;
  isLoading: boolean;
} {
  const res = useSWR(
    searchValue != null ? ['LOOKUP', 'USER_INFO', searchValue] : null,
    async ([, , searchValue]) => {
      try {
        const result = await apiClient.getUsers({
          search: searchValue,
          limit,
        });
        return result.data.map((u) => UserOverviewSchema.parse(u));
      } catch (error) {
        if (error instanceof HTTPFetchError && error.status === 404) {
          return null;
        }
      }
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
      keepPreviousData: true,
      ...options,
    },
  );

  return {
    users: res.data,
    error: res.error,
    isLoading: res.isLoading,
  };
}

export function useOutsideEvent(
  ref: RefObject<any>,
  onOutsideClick: (event: MouseEvent) => void,
  protectedElements: Array<RefObject<any>> = [],
  dependencies: Array<any> = [],
) {
  useEffect(() => {
    /**
     * Trigger event, if you click outside of the element
     */
    function handleClickOutside(event: MouseEvent) {
      if (
        ref.current &&
        !ref.current.contains(event.target) &&
        !protectedElements.find((e) => e.current.contains(event.target))
      ) {
        onOutsideClick(event);
      }
    }

    // Bind the event listener
    document.addEventListener('click', handleClickOutside);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener('click', handleClickOutside);
    };
  }, [ref, ...dependencies]);
}

export function useQuery(): URLSearchParams {
  const { search } = useLocation();
  return React.useMemo(() => new URLSearchParams(search), [search]);
}

// TODO: replace it with native useId hook in react@18
export function useId(): string {
  const [id] = useState(shortIdBuilder());

  return id;
}

/**
 * Returns the current scroll position of the window.
 *
 * @returns {number} The current scroll position of the window.
 */
export function useOnScroll(): number {
  const [scrollPosition, setScrollPosition] = useState(0);

  const handleScroll = () => {
    const position = window.pageYOffset;
    setScrollPosition(position);
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return scrollPosition;
}

/**
 * Returns the position of a given element relative to the viewport.
 *
 * @param {RefObject<HTMLElement>} ref - A reference to the element to track.
 * @returns {DOMRect} The position of the element relative to the viewport.
 */
export function useElementPosition(ref: RefObject<HTMLElement>): DOMRect {
  const [elementPosition, setElementPosition] = useState<DOMRect>(() => new DOMRect());
  const { visible } = useElementDimensions(ref);

  const handlePositionChange = () => {
    if (ref.current) {
      setElementPosition(ref.current.getBoundingClientRect());
    }
  };

  useLayoutEffect(() => {
    window.addEventListener('scroll', handlePositionChange, true);

    return () => {
      window.removeEventListener('scroll', handlePositionChange);
    };
  }, []);

  useEffect(handlePositionChange, [visible]);

  return elementPosition;
}

/**
 * Reactive document.activeElement, returns a reference to current active element
 *
 * @returns current active element (DOM node)
 **/
export function useActiveElement() {
  const [activeElement, setActiveElement] = useState(() => document?.activeElement);
  const documentRef = useRef<Document>(document);

  useEventListener('focus', () => setActiveElement(document?.activeElement), documentRef, true);
  useEventListener('blur', () => setActiveElement(null), documentRef, true);

  return { activeElement };
}

/**
 * Return the platform of the current device
 *
 * @returns Android | IOS | UNKNOWN | Mac | Windows | Linux
 **/
export function usePlatform(): Platforms {
  return useMemo(() => {
    const userAgent = navigator.userAgent;
    return getPlatformOfUserAgent(userAgent);
  }, []);
}

export const basePriceMapping: Record<EventTypes, StripeProducts | null> = {
  [EventTypes.REGULAR]: StripeProducts.BASE_PRICE,
  [EventTypes.STAGE]: StripeProducts.BASE_PRICE,
  [EventTypes.STAGE_GROUP]: null,
  [EventTypes.CONTEST]: StripeProducts.BASE_PRICE,
  [EventTypes.CONTEST_GROUP]: null,
  [EventTypes.SAFEMAP]: StripeProducts.SAFEMAP_BASE_PRICE,
};

export function useMonthlyInvoices({ month, userId }: { month: string; userId?: ObjectId }): {
  invoices?: Array<InvoiceSummary> | null;
  isLoading: boolean;
  error: Error;
} {
  const {
    data: invoices,
    isLoading,
    error,
  } = useSWR(
    ['GET', 'MONTHLY_INVOICE', month, userId?.toHexString()],
    async ([, , month, userId]) => {
      try {
        return await apiClient.getMonthlyInvoice({ month, userId });
      } catch (error) {
        if (error instanceof HTTPFetchError && error.status === 404) {
          return null;
        }
        throw error;
      }
    },
    {
      revalidateIfStale: false,
      revalidateOnFocus: false,
      refreshInterval: OneMinuteInMillis * 10,
    },
  );

  return { invoices, isLoading, error };
}

/**
 * Returns the shadowtrack of an event
 *
 * @returns {alerts: Array<EntityEvent> | undefined, isLoading: boolean, error: Error}
 */
export function useShadowtrack(
  event: Immutable<{ geo: { shadowtrackId: string | null } }> | null | undefined,
): {
  shadowtrack: Shadowtrack | undefined | null;
  isLoading: boolean;
  error: Error;
} {
  const { data, isLoading, error } = useSWR(
    event?.geo.shadowtrackId != null
      ? ['GET', 'SHADOWTRACK', event.geo.shadowtrackId?.toString()]
      : null,
    async ([, , shadowtrackId]) => {
      try {
        return await apiClient.getGeoLineString(shadowtrackId);
      } catch (error) {
        if (error instanceof HTTPFetchError && error.status === 404) {
          return null;
        }
      }
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
      keepPreviousData: true,
    },
  );

  return { shadowtrack: data, isLoading, error };
}

/**
 * Returns the event for a slug or an eventId
 *
 * @returns {event: RacemapEvent | undefined, isLoading: boolean, error: Error}
 */
export function useEvent(
  { slug, eventId }: { slug?: string; eventId?: string | ObjectId },
  options?: SWRConfiguration,
): {
  event: RacemapEvent | null | undefined;
  isLoading: boolean;
  error: Error;
} {
  const { data, isLoading, error } = useSWR(
    eventId != null || slug != null ? ['GET', 'EVENT', eventId || slug] : null,
    async ([, , eventIdOrSlug]) => {
      if (eventIdOrSlug == null) return null;
      const event = await apiClient.getEvent(eventIdOrSlug?.toString());

      return event;
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
      keepPreviousData: true,
      ...options,
    },
  );

  return { event: data, isLoading, error };
}

/**
 * Custom hook to fetch Stripe customer information for a given user.
 *
 * @param userId - The ID of the user, which can be an ObjectId, string, or null.
 * @returns An object containing the Stripe customer information, loading state, and any error encountered.
 *
 * @property {StripeCustomerInfos | undefined} stripeUser - The fetched Stripe customer information, or undefined if not loaded.
 * @property {boolean} isLoading - A boolean indicating if the data is currently being loaded.
 * @property {Error} error - An error object if an error occurred during the fetch operation.
 */
export function useStripeCustomer(userId: ObjectId | string | null): {
  stripeUser: StripeCustomerInfos | undefined;
  isLoading: boolean;
  error: Error;
} {
  const { data, isLoading, error } = useSWR(
    userId != null ? ['GET', 'STRIPE_USER', userId] : null,
    async ([, , userId]) => {
      return await apiClient.getStripeUser(userId.toString());
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );

  return { stripeUser: data, isLoading, error };
}

export const useGetEventPointsRaw = (eventId: string | ObjectId | null | undefined) => {
  const { data, isLoading, error } = useSWR(
    eventId ? ['GET', 'EVENT_POINTS_RAW', eventId.toString()] : null,
    async ([, , id]) => {
      return await apiClient.getEventPointsRaw(id.toString());
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );
  return { eventPointsRaw: data, isLoading, error };
};

export const useGetEventSplits = (eventId: string | ObjectId | null | undefined) => {
  const { data, isLoading, error } = useSWR(
    eventId ? ['GET', 'EVENT_SPLITS', eventId.toString()] : null,
    async ([, , id]) => {
      return await apiClient.getEventSplits(id.toString());
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );
  return { eventSplits: data, isLoading, error };
};

export function useShadowtrackSimple(eventId: string | ObjectId | null | undefined): {
  shadowtrackSimple?: EventTrackObject;
  isLoading: boolean;
  error: Error | undefined;
} {
  const { data, isLoading, error } = useSWR(
    eventId ? ['GET', 'EVENT_SHADOW_GEO', eventId.toString()] : null,
    async ([, , id]) => {
      const response = await apiClient.getEventShadowGeo(id.toString());
      return response.features[0];
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );
  return { shadowtrackSimple: data, isLoading, error };
}

export function useShadowtrackCoordinatesChunked(eventId: string | ObjectId | null | undefined): {
  shadowtrackCoordinatesChunked?: Array<LatLng>;
  isLoading: boolean;
  error: Error | undefined;
} {
  const { data, isLoading, error } = useSWR(
    eventId ? ['GET', 'EVENT_CHUNKED_SHADOW_GEO', eventId.toString()] : null,
    async ([, , id]) => {
      return await apiClient.asAnonymous().getEventChunkedShadowGeo(id.toString());
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );
  return { shadowtrackCoordinatesChunked: data, isLoading, error };
}

export function useEventChunkedShadowGeo(eventId: string | ObjectId | null | undefined): {
  chunkedShadowtrack?: Array<LatLng>;
  isLoading: boolean;
  error: Error | undefined;
} {
  const { data, isLoading, error } = useSWR(
    eventId ? ['GET', 'EVENT_CHUNKED_SHADOW_GEO_NON_ANONYMOUS', eventId.toString()] : null,
    async ([, , id]) => {
      return await apiClient.getEventChunkedShadowGeo(id.toString());
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 5,
    },
  );

  return { chunkedShadowtrack: data, isLoading, error };
}

/**
 * Custom hook to fetch user infos (name + email) for a list of user IDs.
 *
 * @param userIds - Array of user IDs (string).
 * @returns A mapping { [userId]: { name, email } }, loading state, and error.
 */
export function useUserInfos(userIds: Immutable<Array<string>>): {
  userInfos: Record<string, { name: string; email?: string | null; parentId?: string | null }>;
  isLoading: boolean;
  error?: Error;
} {
  const uniqueIds = Array.from(new Set(userIds));

  const key = uniqueIds.length > 0 ? ['GET', 'USER_INFOS', ...uniqueIds] : null;

  const { data, error, isLoading } = useSWR(
    key,
    async () => {
      const entries = await Promise.all(
        uniqueIds.map(async (id) => {
          try {
            const result = await apiClient.userLookup({ userId: id });
            const parsed = UserOverviewSchema.parse(result);
            return [
              id,
              {
                name: parsed.name ?? id,
                email: parsed.email ?? null,
                parentId: parsed.parentId?.toHexString() ?? null,
              },
            ];
          } catch (err) {
            console.error(`Failed to fetch user info for ID ${id}:`, err);
            return [id, { name: id, email: null, parentId: null }];
          }
        }),
      );
      return Object.fromEntries(entries);
    },
    {
      revalidateIfStale: false,
      refreshInterval: OneMinuteInMillis * 10,
      keepPreviousData: true,
    },
  );

  return {
    userInfos: data ?? {},
    isLoading,
    error,
  };
}
